import { OrderStatusEnum } from '@authority-labs/enums';

/**
 * Order statuses in which the inspection itself may still be edited by a user
 * without the inspections.manage permission.
 */
export const INSPECTION_EDITABLE_ORDER_STATUSES: OrderStatusEnum[] = [
  OrderStatusEnum.InspectionInProgress,
  OrderStatusEnum.InspectionInProgressNoPlans,
  OrderStatusEnum.InspectionInProgressPartialPlans,
  OrderStatusEnum.WaitingForInspection,
  OrderStatusEnum.WaitingForCustomerDocs
];

/** Who is asking, for the review rule. */
export interface InspectionEditContext {
  /** The signed-in user's username. */
  username?: string;
  /** The order's reviewer (a username), if one is assigned. */
  reviewedBy?: string | null;
  /**
   * The user is a tester or senior tester. In production testers hold
   * inspections.manage too, so in review "manager" means a non-tester with it.
   */
  isTester?: boolean;
}

/**
 * Whether an inspection is readonly given the user's permission, the current
 * status of its order and, in review, who is asking. Shared by the UI and the
 * API so both enforce the same rule:
 * - a closed order is readonly even for managers;
 * - while the order waits for review its reviewer edits, and so do managers
 *   who are not testers (inspections.manage); every other tester, the
 *   assignee included, is readonly whatever their permissions;
 * - managers (inspections.manage) edit in every other status;
 * - otherwise the inspection is editable in INSPECTION_EDITABLE_ORDER_STATUSES.
 */
export function isInspectionReadonly(
  hasManagePermission: boolean,
  orderStatusKey: string | undefined,
  context: InspectionEditContext = {}
): boolean {
  const key = orderStatusKey as OrderStatusEnum;
  if (key === OrderStatusEnum.Closed) return true;
  if (key === OrderStatusEnum.WaitingForReview) {
    const isReviewer =
      !!context.reviewedBy &&
      !!context.username &&
      context.reviewedBy === context.username;
    return !(isReviewer || (hasManagePermission && !context.isTester));
  }
  if (hasManagePermission) return false;
  return !INSPECTION_EDITABLE_ORDER_STATUSES.includes(key);
}
